import trackClevertapEvents from "@/utils/trackClevertapEvents";
import {
  Search,
  SingleSelectDropdown,
  SuccessFilled,
  Toast,
} from "@monsterindia/global-ui-components";
import React, { useCallback, useEffect, useState } from "react";

import {
  blockSelectedCompany,
  getBlockCompaniesList,
  masterDataSearch,
  unblockCompany,
} from "@/components/settings/api/networkUtils";
import LoadingSkeleton from "@/components/settings/components/common/LoadingSkeleton";
import { useProfileData } from "@/components/settings/contents/profileSettings";
import {
  BlockedCompany,
  MasterDataResponse,
} from "@/components/settings/types/setting";

import BlockCompanyMobileModal from "./BlockCompanyMobileModal";
import BlockedCompaniesList from "./BlockedCompaniesList";

const BlockCompany = ({ isMobile }: { isMobile: boolean }) => {
  const { toastErrorMsg, staticData } = useProfileData?.blockCompany ?? {};
  const [companyList, setCompanyList] = useState<MasterDataResponse>();
  const [blockCompanyList, setBlockCompanyList] = useState<BlockedCompany[]>(
    [],
  );
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState({ text: "", type: "", show: false });
  const [companyListLoading, setCompanyListLoading] = useState(true);

  const fetchBlockCompaniesList = useCallback(async () => {
    try {
      const data = await getBlockCompaniesList();
      const companyList = data?.data ?? [];
      setBlockCompanyList(companyList);
    } catch (e) {
      console.error("Error fetching blocked companies list:", e);
      setBlockCompanyList([]);
    } finally {
      setCompanyListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlockCompaniesList();
  }, [fetchBlockCompaniesList]);

  const onChangeHandler = async (val: string) => {
    const masterData: any = await masterDataSearch("COMPANY", val);
    const companies = masterData?.data ?? [];

    setCompanyList(
      companies.map((company: any) => ({
        id: company?.id ?? 0,
        text: company?.name ?? "Unnamed Company",
      })),
    );
  };

  const handleToast = (message: string, type: string) => {
    setToast({ text: message, type, show: true });
  };

  const unblockCompanyHandler = async (item: UnblockCompanyHandlerProps) => {
    try {
      const response = await unblockCompany(item?.id);
      if (response) {
        handleToast(
          `${item?.company?.text || "Company"} has successfully been unblocked`,
          "success",
        );
        fetchBlockCompaniesList();
        trackClevertapEvents.eventPush("Settings Updated", {
          blockCompanies: item?.company?.text,
        });
      }
    } catch (error) {
      console.error("Error unblocking company:", error);
      handleToast(toastErrorMsg, "error");
    }
  };

  const handleDropdownSelect = async (val: OptionType | null) => {
    const blockCompanyListName = blockCompanyList?.map(
      (item) => item?.company?.text as string,
    );
    const blockedVal = val?.text as string;

    if (blockCompanyListName?.includes(blockedVal)) {
      handleToast("Company already in the Block List", "error");
      return;
    }
    if (val) {
      try {
        const response = await blockSelectedCompany(val);
        if (response) {
          handleToast(
            `${val?.text || "Company"} has successfully been blocked`,
            "success",
          );
          fetchBlockCompaniesList();
          trackClevertapEvents.eventPush("Settings Updated", {
            blockCompanies: `${val?.text}`,
          });
        }
      } catch (error) {
        handleToast(toastErrorMsg, "error");
      }
    }
  };

  const handleModal = () => setShowModal((prev) => !prev);
  const openModalHandler = () => setShowModal(true);

  const dropdownArgs = {
    width: "w-full",
    isSearchable: true,
    className: "!pl-9",
    placeholder: "Enter company name",
    leftIcon: <Search fill="#A5A3AE" />,
    optionRightIcon: <SuccessFilled fill="#228568" />,
    options: companyList ? companyList : [],
    errorText: "",
  };

  const LoadingOrBlockedList = () =>
    companyListLoading ? (
      <LoadingSkeleton
        rows={2}
        primaryClass={`mt-3 m-1 flex gap-2 flex-col p-2 ${isMobile ? "rounded-xl bg-surface-primary-normal" : ""}`}
        secondryClass="rounded bg-[#e7e7e7] py-5 m-1"
      />
    ) : (
      <BlockedCompaniesList
        isMobile={isMobile}
        blockCompanyList={blockCompanyList}
        unblockCompanyHandler={unblockCompanyHandler}
      />
    );

  return (
    <div className="bg-surface-secondary flex flex-col rounded-xl p-3">
      <div className="pb-2 text-base font-semibold">
        {" "}
        {staticData?.title || "Default Title"}
      </div>
      <div className="text-content-secondary text-sm font-normal md:text-xs">
        {staticData?.description || "No description available."}
      </div>
      {isMobile ? (
        <>
          <div
            onClick={openModalHandler}
            className="text-content-strong pb-3 pt-5 text-sm font-semibold"
          >
            {staticData?.addButtonText || "Add"}
          </div>
          <LoadingOrBlockedList />
          <BlockCompanyMobileModal
            isMobile={isMobile}
            showModal={showModal}
            handleModal={handleModal}
            args={dropdownArgs}
            handleDropdownSelect={handleDropdownSelect}
            onChangeHandler={onChangeHandler}
            blockCompanyList={blockCompanyList}
            unblockCompanyHandler={unblockCompanyHandler}
          />
        </>
      ) : (
        <>
          <div className="bg-surface-primary-normal mt-3 rounded-xl">
            <SingleSelectDropdown
              {...dropdownArgs}
              handleDropdownSelect={handleDropdownSelect}
              onChangeHandler={onChangeHandler}
            />
          </div>
          <LoadingOrBlockedList />
        </>
      )}
      <Toast
        timer={3000}
        showToast={toast.show}
        text={toast.text}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
        width={isMobile ? "75" : "50"}
        position="top-center"
      />
    </div>
  );
};

export default BlockCompany;
